/**
 * Acciones del mapa mental: cada una muta el árbol Y corre el layout dentro de
 * la misma transacción, así el deshacer revierte estructura y posiciones juntas
 * y los demás clientes reciben el mapa ya acomodado.
 */
import type { BoardKind, Shape, ShapeId, TopicShape } from '../../shared/types.ts'
import type { BoardDoc, TopicLayout } from '../state/board.ts'
import { newId } from '../state/shapes.ts'
import { fontString, wrapText } from '../canvas/text.ts'
import { canReparent, layoutMindMap, subtreeIds } from './layout.ts'

/** Tamaño de letra por profundidad (0 = tema central). */
export function topicFontSize(depth: number): number {
  return depth === 0 ? 22 : depth === 1 ? 16 : 14
}

/** Relleno interior por profundidad. */
export function topicPadding(depth: number): { px: number; py: number } {
  return depth === 0 ? { px: 22, py: 13 } : depth === 1 ? { px: 14, py: 9 } : { px: 10, py: 6 }
}

const MAX_TOPIC_WIDTH = 260

let measureCtx: CanvasRenderingContext2D | null = null

/** Mide un tema con la misma tipografía con la que se dibuja. */
export function measureTopic(text: string, depth: number): { w: number; h: number } {
  if (!measureCtx) {
    measureCtx = document.createElement('canvas').getContext('2d')
  }
  const fontSize = topicFontSize(depth)
  const { px, py } = topicPadding(depth)
  const ctx = measureCtx
  if (!ctx) return { w: 120, h: 40 } // entorno sin canvas (pruebas)
  ctx.font = fontString(fontSize, depth === 0)
  const value = text || ' '
  const lines = wrapText(ctx, value, MAX_TOPIC_WIDTH, fontSize, depth === 0)
  let widest = 0
  for (const line of lines) widest = Math.max(widest, ctx.measureText(line).width)
  return {
    w: Math.max(depth === 0 ? 96 : 56, Math.ceil(widest) + px * 2),
    h: Math.ceil(lines.length * fontSize * 1.3) + py * 2,
  }
}

function topicsOf(board: BoardDoc): TopicShape[] {
  return board.all().filter((s): s is TopicShape => s.type === 'topic')
}

/**
 * Recoloca todo el mapa y escribe posiciones y campos derivados. Llamar SIEMPRE
 * dentro de una transacción que ya hizo el cambio estructural.
 */
export function relayout(board: BoardDoc, measure = measureTopic): void {
  const topics = topicsOf(board)
  if (topics.length === 0) {
    pruneLayouts(board)
    return
  }
  // Medida acordada: si el tema ya tiene un tamaño medido con este mismo texto
  // y nivel, se reutiliza en vez de volver a medir con la fuente local.
  const porId = new Map(topics.map((t) => [t.id, t]))
  const medida = (text: string, depth: number, id?: ShapeId): { w: number; h: number } => {
    const previo = id ? porId.get(id) : undefined
    if (previo && previo.mText === text && previo.mDepth === depth && previo.w > 0 && previo.h > 0) {
      return { w: previo.w, h: previo.h }
    }
    return measure(text, depth)
  }
  const resultado = layoutMindMap(
    topics.map((t) => ({
      id: t.id,
      parentId: t.parentId,
      order: t.order,
      collapsed: t.collapsed,
      text: t.text,
      // el ancla de una raíz es su CENTRO actual (geometría fusionada)
      x: t.x + t.w / 2,
      y: t.y + t.h / 2,
    })),
    board.mapLayout,
    medida,
  )

  // El motor escribe SOLO la geometría derivada (mapa aparte): jamás la figura.
  // Así no puede resucitar un tema que otro cliente borró ni pisarle el texto.
  const cambios = new Map<ShapeId, TopicLayout>()
  for (const t of topics) {
    const p = resultado.placements.get(t.id)
    if (!p) continue
    if (
      t.x !== p.x ||
      t.y !== p.y ||
      t.w !== p.w ||
      t.h !== p.h ||
      t.depth !== p.depth ||
      t.branchColor !== p.branchColor ||
      !!t.hidden !== p.hidden ||
      (t.hiddenCount ?? 0) !== p.hiddenCount ||
      t.side !== p.side ||
      t.mText !== t.text ||
      t.mDepth !== p.depth
    ) {
      cambios.set(t.id, {
        x: p.x,
        y: p.y,
        w: p.w,
        h: p.h,
        depth: p.depth,
        branchColor: p.branchColor,
        hidden: p.hidden || undefined,
        hiddenCount: p.hiddenCount || undefined,
        side: p.side,
        mText: t.text,
        mDepth: p.depth,
      })
    }
  }
  board.transact(() => {
    for (const [id, layout] of cambios) board.layouts.set(id, layout)
    // Reparación determinista de ciclos (dos recolgados cruzados a la vez):
    // la raíz rescatada por el layout queda como flotante DE VERDAD en el doc.
    // Todos los clientes computan el mismo rescate, así que convergen.
    for (const id of resultado.rescatados) {
      const topic = board.get(id)
      if (topic?.type === 'topic' && topic.parentId !== null) {
        board.shapes.set(id, { ...board.shapes.get(id)!, parentId: null, updatedAt: Date.now() })
      }
    }
    pruneLayouts(board)
  })
  updateRelationBounds(board)
}

/** Basura fuera: layouts sin figura y relaciones con algún extremo borrado. */
function pruneLayouts(board: BoardDoc): void {
  for (const id of Array.from(board.layouts.keys())) {
    if (!board.shapes.has(id)) board.layouts.delete(id)
  }
  const muertas = board
    .all()
    .filter((s) => s.type === 'relation' && (!board.shapes.has(s.from) || !board.shapes.has(s.to)))
    .map((s) => s.id)
  if (muertas.length > 0) for (const id of muertas) board.shapes.delete(id)
}

/** Las relaciones abarcan sus dos extremos: su caja se deriva de los temas. */
function updateRelationBounds(board: BoardDoc): void {
  const relations = board.all().filter((s) => s.type === 'relation')
  if (relations.length === 0) return
  const now = Date.now()
  const changed: Shape[] = []
  for (const r of relations) {
    if (r.type !== 'relation') continue
    const a = board.get(r.from)
    const b = board.get(r.to)
    if (!a || !b) continue
    const x = Math.min(a.x + a.w / 2, b.x + b.w / 2)
    const y = Math.min(a.y + a.h / 2, b.y + b.h / 2)
    const w = Math.max(24, Math.abs(a.x + a.w / 2 - (b.x + b.w / 2)))
    const h = Math.max(24, Math.abs(a.y + a.h / 2 - (b.y + b.h / 2)))
    const hidden = (a.type === 'topic' && a.hidden) || (b.type === 'topic' && b.hidden) || undefined
    if (r.x !== x || r.y !== y || r.w !== w || r.h !== h || !!r.hidden !== !!hidden) {
      changed.push({ ...r, x, y, w, h, hidden, updatedAt: now } as Shape)
    }
  }
  if (changed.length > 0) board.putMany(changed)
}

function baseTopic(creator: string, parentId: ShapeId | null, order: number, text: string): TopicShape {
  return {
    id: newId('t'),
    type: 'topic',
    parentId,
    order,
    text,
    x: 0,
    y: 0,
    w: 10,
    h: 10,
    rotation: 0,
    z: 1,
    depth: parentId ? 1 : 0,
    branchColor: '#26262B',
    createdBy: creator,
    updatedAt: Date.now(),
  }
}

function siblingsOf(board: BoardDoc, parentId: ShapeId | null): TopicShape[] {
  return topicsOf(board)
    .filter((t) => t.parentId === parentId)
    .sort((a, b) => a.order - b.order)
}

/** Crea el tema central de un mapa vacío (id fijo: dos clientes convergen). */
export function seedMindMap(board: BoardDoc, creator: string, centerX: number, centerY: number): void {
  board.transact(() => {
    if (topicsOf(board).length > 0) return
    // El nombre del documento debe coincidir con el del servidor.
    if (!board.meta.get('name')) board.meta.set('name', 'Mapa mental sin título')
    const root: TopicShape = {
      ...baseTopic(creator, null, 0, 'Tema central'),
      id: 'topic-root',
      x: centerX,
      y: centerY,
    }
    board.put(root)
    relayout(board)
  })
}

/** Agrega un hijo al final y lo devuelve (para seleccionarlo y editarlo). */
export function addChild(board: BoardDoc, creator: string, parentId: ShapeId, text = 'Tema'): TopicShape | null {
  const parent = board.get(parentId)
  if (parent?.type !== 'topic') return null
  const siblings = siblingsOf(board, parentId)
  const order = siblings.length > 0 ? siblings[siblings.length - 1].order + 1 : 0
  const child = baseTopic(creator, parentId, order, text)
  board.undoManager.stopCapturing()
  board.transact(() => {
    // Agregar bajo un tema plegado lo despliega: si no, el hijo nace invisible.
    if (parent.collapsed) board.put({ ...parent, collapsed: undefined, updatedAt: Date.now() })
    board.put(child)
    relayout(board)
  })
  return child
}

/** Agrega un hermano justo después del tema dado. */
export function addSibling(board: BoardDoc, creator: string, id: ShapeId, text = 'Tema'): TopicShape | null {
  const topic = board.get(id)
  if (topic?.type !== 'topic') return null
  // El tema central no tiene hermanos: darle Enter crea un hijo.
  if (topic.parentId === null) return addChild(board, creator, id, text)
  const siblings = siblingsOf(board, topic.parentId)
  const index = siblings.findIndex((s) => s.id === id)
  const next = siblings[index + 1]
  const order = next ? (topic.order + next.order) / 2 : topic.order + 1
  const sibling = baseTopic(creator, topic.parentId, order, text)
  board.undoManager.stopCapturing()
  board.transact(() => {
    board.put(sibling)
    relayout(board)
  })
  return sibling
}

/** Cambia el texto y reacomoda (el tamaño del tema depende del texto). */
export function setTopicText(board: BoardDoc, id: ShapeId, text: string): void {
  const topic = board.get(id)
  if (topic?.type !== 'topic') return
  board.transact(() => {
    board.put({ ...topic, text, updatedAt: Date.now() })
    relayout(board)
  })
}

/** Pliega o despliega una rama. */
export function toggleCollapse(board: BoardDoc, id: ShapeId): void {
  const topic = board.get(id)
  if (topic?.type !== 'topic') return
  board.undoManager.stopCapturing()
  board.transact(() => {
    board.put({ ...topic, collapsed: topic.collapsed ? undefined : true, updatedAt: Date.now() })
    relayout(board)
  })
}

/** Color a mano para la rama que nace en este tema. */
export function setTopicColor(board: BoardDoc, id: ShapeId, color: string | null): void {
  const topic = board.get(id)
  if (topic?.type !== 'topic') return
  board.transact(() => {
    board.put({ ...topic, color: color ?? undefined, updatedAt: Date.now() })
  })
}

/** Borra un tema con TODO su subárbol y las relaciones que lo tocaban. */
export function deleteSubtree(board: BoardDoc, id: ShapeId): void {
  const topics = topicsOf(board)
  const doomed = subtreeIds(topics, id)
  const relations = board
    .all()
    .filter((s) => s.type === 'relation' && (doomed.has(s.from) || doomed.has(s.to)))
    .map((s) => s.id)
  board.undoManager.stopCapturing()
  board.transact(() => {
    board.delete([...doomed, ...relations])
    relayout(board)
  })
}

/**
 * Cuelga `id` de `targetId` (arrastrar y soltar). Elige el orden entre los
 * hijos del destino según la posición del punto de suelta. Devuelve false si
 * el movimiento crearía un ciclo.
 */
export function reparent(
  board: BoardDoc,
  id: ShapeId,
  targetId: ShapeId,
  dropX: number,
  dropY: number,
): boolean {
  const topics = topicsOf(board)
  if (!canReparent(topics, id, targetId)) return false
  const topic = board.get(id)
  const target = board.get(targetId)
  if (topic?.type !== 'topic' || target?.type !== 'topic') return false

  // Orden: se inserta según dónde cayó respecto a los hijos actuales.
  const siblings = siblingsOf(board, targetId).filter((s) => s.id !== id)
  const vertical = board.mapLayout === 'down'
  let order: number
  if (siblings.length === 0) {
    order = 0
  } else {
    const pos = vertical ? dropX : dropY
    let index = siblings.length
    for (let i = 0; i < siblings.length; i++) {
      const s = siblings[i]
      const center = vertical ? s.x + s.w / 2 : s.y + s.h / 2
      if (pos < center) {
        index = i
        break
      }
    }
    if (index === 0) order = siblings[0].order - 1
    else if (index >= siblings.length) order = siblings[siblings.length - 1].order + 1
    else order = (siblings[index - 1].order + siblings[index].order) / 2
  }

  board.undoManager.stopCapturing()
  board.transact(() => {
    if (target.collapsed) board.put({ ...target, collapsed: undefined, updatedAt: Date.now() })
    board.put({ ...topic, parentId: targetId, order, updatedAt: Date.now() })
    relayout(board)
  })
  return true
}

/** Suelta un tema en el vacío: si era hijo, vuelve a su sitio; si es raíz, se ancla ahí. */
export function settleDrop(board: BoardDoc, id: ShapeId): void {
  const topic = board.get(id)
  if (topic?.type !== 'topic') return
  board.transact(() => {
    relayout(board)
  })
}

/** Tema flotante nuevo (raíz de su propio árbol) en el punto dado. */
export function addFloating(board: BoardDoc, creator: string, x: number, y: number, text = 'Tema flotante'): TopicShape {
  const topic: TopicShape = { ...baseTopic(creator, null, 0, text), x, y }
  board.undoManager.stopCapturing()
  board.transact(() => {
    board.put(topic)
    relayout(board)
  })
  return topic
}

/** Copia un subárbol completo como árbol flotante, desplazado. */
export function duplicateSubtree(board: BoardDoc, creator: string, id: ShapeId): TopicShape | null {
  const topics = topicsOf(board)
  const ids = subtreeIds(topics, id)
  const originals = topics.filter((t) => ids.has(t.id))
  const root = originals.find((t) => t.id === id)
  if (!root) return null
  const remap = new Map<ShapeId, ShapeId>()
  for (const t of originals) remap.set(t.id, newId('t'))
  const now = Date.now()
  const copies: TopicShape[] = originals.map((t) => ({
    ...t,
    id: remap.get(t.id)!,
    parentId: t.id === id ? null : remap.get(t.parentId!)!,
    x: t.x + 60,
    y: t.y + 60,
    createdBy: creator,
    updatedAt: now,
  }))
  board.undoManager.stopCapturing()
  board.transact(() => {
    board.putMany(copies)
    relayout(board)
  })
  return board.get(remap.get(id)!) as TopicShape
}

/** Cambia la estructura del mapa (balanceado / lógico / organigrama). */
export function setMapLayout(board: BoardDoc, layout: BoardMetaLayout): void {
  board.undoManager.stopCapturing()
  board.transact(() => {
    board.setMapLayout(layout)
    relayout(board)
  })
}
type BoardMetaLayout = Parameters<BoardDoc['setMapLayout']>[0]

/** Crea una relación (flecha punteada) entre dos temas. */
export function addRelation(board: BoardDoc, creator: string, from: ShapeId, to: ShapeId): void {
  if (from === to) return
  const a = board.get(from)
  const b = board.get(to)
  if (a?.type !== 'topic' || b?.type !== 'topic') return
  // No duplicar la misma relación en el mismo sentido ni en el contrario.
  const exists = board
    .all()
    .some((s) => s.type === 'relation' && ((s.from === from && s.to === to) || (s.from === to && s.to === from)))
  if (exists) return
  board.undoManager.stopCapturing()
  board.transact(() => {
    board.put({
      id: newId('r'),
      type: 'relation',
      from,
      to,
      x: Math.min(a.x, b.x),
      y: Math.min(a.y, b.y),
      w: 10,
      h: 10,
      rotation: 0,
      z: 0.5,
      createdBy: creator,
      updatedAt: Date.now(),
    })
    updateRelationBounds(board)
  })
}

/** ¿Este tablero es un mapa mental? */
export function isMindMap(kind: BoardKind): boolean {
  return kind === 'mindmap'
}
